// ✅ src/pages/VehicleV2/WorkLocationMaster.jsx  (FULL FILE)
// Backend mount:
//   app.use("/api/workLocation", workLocation);
//
// APIs used:
//   GET  https://nissi-india.com/api/workLocation/init?loginUserId=..&branchId=..&workLocationId=..
//   GET  https://nissi-india.com/api/workLocation/states?countryid=..
//   GET  https://nissi-india.com/api/workLocation/districts?stateid=..
//   GET  https://nissi-india.com/api/workLocation/cities?districtid=..
//   POST https://nissi-india.com/api/workLocation/save
//
// Routing:
//   Add : /vehicle/WorkLocationMaster
//   Edit: /vehicle/WorkLocationMaster?worklocationid=123
//
// ✅ Updated pattern:
// - common localStorage/sessionStorage header pattern
// - inventory header support
// - init based loading
// - add/edit mode
// - district -> city flow only
// - loading + pageLoading + loadingDDL states
// - same UI structure style
// - back button goes to /vehicle/WorkLocationListing

import React, {
  useEffect,
  useMemo,
  useState,
  useContext,
  useCallback,
} from "react";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { FaSave, FaUndo, FaArrowLeft } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/workLocationEntry";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === undefined || v === null) return def;
  return String(v).trim();
};

const emptyForm = {
  workLocName: "",
  address1: "",
  address2: "",
  countryId: "",
  stateId: "",
  districtId: "",
  cityId: "",
  zipcode: "",
  contactPerson: "",
  email: "",
  mobile: "",
  phone: "",
  remarks: "",
  code: "",
  isClosed: false,
};

const WorkLocationMaster = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ shared localStorage / sessionStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("branchid") ||
    "";
  const branch =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const workLocationId = useMemo(() => {
    return toInt(
      searchParams.get("worklocationid") ||
        searchParams.get("workLocationId") ||
        searchParams.get("id"),
      0
    );
  }, [searchParams]);

  const isEdit = workLocationId > 0;

  // dropdowns
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);

  // form
  const [formData, setFormData] = useState(emptyForm);

  // loading
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [loadingDDL, setLoadingDDL] = useState(false);

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const inputClass = `w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const selectClass = inputClass;

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const fillFormFromData = useCallback((w, codeFromInit = "") => {
    setFormData({
      workLocName: toStr(w?.workLocName),
      address1: toStr(w?.address1),
      address2: toStr(w?.address2),
      countryId: w?.countryId != null ? String(w.countryId) : "",
      stateId: w?.stateId != null ? String(w.stateId) : "",
      districtId: w?.districtId != null ? String(w.districtId) : "",
      cityId: w?.cityId != null ? String(w.cityId) : "",
      zipcode: toStr(w?.zipcode),
      contactPerson: toStr(w?.contactPerson),
      email: toStr(w?.email),
      mobile: toStr(w?.mobile),
      phone: toStr(w?.phone),
      remarks: toStr(w?.remarks),
      code: toStr(w?.code || codeFromInit),
      isClosed:
        w?.isClosed === true ||
        String(w?.isClosed) === "1" ||
        String(w?.isClosed).toLowerCase() === "true",
    });
  }, []);

  /* ------------------------- INIT LOAD ------------------------- */
  const loadInit = useCallback(async () => {
    try {
      if (!loginUserId) {
        toast.error("Login user not found");
        return;
      }

      setPageLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          loginUserId,
          branchId,
          workLocationId: isEdit ? workLocationId : undefined,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to load initial data");
      }

      setCountries(Array.isArray(data?.countries) ? data.countries : []);
      setStates(Array.isArray(data?.states) ? data.states : []);
      setDistricts(Array.isArray(data?.districts) ? data.districts : []);
      setCities(Array.isArray(data?.cities) ? data.cities : []);

      if (data?.workLocation) {
        fillFormFromData(data.workLocation, data.code || "");
      } else {
        setFormData({
          ...emptyForm,
          code: toStr(data?.code),
        });
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load initial data"
      );
    } finally {
      setPageLoading(false);
    }
  }, [
    loginUserId,
    branchId,
    isEdit,
    workLocationId,
    commonHeaders,
    fillFormFromData,
  ]);

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  /* ------------------------- CASCADING DROPDOWNS ------------------------- */
  const loadStates = useCallback(
    async (countryId) => {
      try {
        setLoadingDDL(true);

        const { data } = await axios.get(`${API_BASE}/states`, {
          params: { countryid: countryId },
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load states");
        }

        setStates(Array.isArray(data?.list) ? data.list : []);
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message || err?.message || "Failed to load states"
        );
        setStates([]);
      } finally {
        setLoadingDDL(false);
      }
    },
    [commonHeaders]
  );

  const loadDistricts = useCallback(
    async (stateId) => {
      try {
        setLoadingDDL(true);

        const { data } = await axios.get(`${API_BASE}/districts`, {
          params: { stateid: stateId },
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load districts");
        }

        setDistricts(Array.isArray(data?.list) ? data.list : []);
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load districts"
        );
        setDistricts([]);
      } finally {
        setLoadingDDL(false);
      }
    },
    [commonHeaders]
  );

  const loadCities = useCallback(
    async (districtId) => {
      try {
        setLoadingDDL(true);

        const { data } = await axios.get(`${API_BASE}/cities`, {
          params: { districtid: districtId },
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load cities");
        }

        setCities(Array.isArray(data?.list) ? data.list : []);
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message || err?.message || "Failed to load cities"
        );
        setCities([]);
      } finally {
        setLoadingDDL(false);
      }
    },
    [commonHeaders]
  );

  const onCountryChange = async (e) => {
    const countryId = e.target.value;

    setFormData((prev) => ({
      ...prev,
      countryId,
      stateId: "",
      districtId: "",
      cityId: "",
    }));

    setStates([]);
    setDistricts([]);
    setCities([]);

    if (!countryId) return;
    await loadStates(countryId);
  };

  const onStateChange = async (e) => {
    const stateId = e.target.value;

    setFormData((prev) => ({
      ...prev,
      stateId,
      districtId: "",
      cityId: "",
    }));

    setDistricts([]);
    setCities([]);

    if (!stateId) return;
    await loadDistricts(stateId);
  };

  const onDistrictChange = async (e) => {
    const districtId = e.target.value;

    setFormData((prev) => ({
      ...prev,
      districtId,
      cityId: "",
    }));

    setCities([]);

    if (!districtId) return;
    await loadCities(districtId);
  };

  /* ------------------------- VALIDATION ------------------------- */
  const canSave = useMemo(() => {
    return (
      toStr(formData.workLocName) &&
      toStr(formData.address1) &&
      toStr(formData.countryId) &&
      toStr(formData.stateId) &&
      toStr(formData.districtId) &&
      toStr(formData.cityId) &&
      toStr(formData.contactPerson) &&
      toStr(formData.phone) &&
      toStr(formData.code)
    );
  }, [formData]);

  const validate = () => {
    if (!toStr(formData.workLocName)) {
      toast.error("Please enter Work Location Name");
      return false;
    }
    if (!toStr(formData.address1)) {
      toast.error("Please enter Address1");
      return false;
    }
    if (!toStr(formData.countryId)) {
      toast.error("Please select Country");
      return false;
    }
    if (!toStr(formData.stateId)) {
      toast.error("Please select State");
      return false;
    }
    if (!toStr(formData.districtId)) {
      toast.error("Please select District");
      return false;
    }
    if (!toStr(formData.cityId)) {
      toast.error("Please select City");
      return false;
    }
    if (!toStr(formData.contactPerson)) {
      toast.error("Please enter Contact Person");
      return false;
    }
    if (!toStr(formData.phone)) {
      toast.error("Please enter Phone");
      return false;
    }
    if (!toStr(formData.code)) {
      toast.error("Code not generated");
      return false;
    }
    if (!toInt(branchId)) {
      toast.error("branchId not found");
      return false;
    }
    if (!toInt(loginUserId)) {
      toast.error("loginUserId not found");
      return false;
    }
    return true;
  };

  /* ------------------------- ACTIONS ------------------------- */
  const handleReset = () => {
    if (isEdit) {
      loadInit();
      return;
    }

    setFormData(emptyForm);
    setStates([]);
    setDistricts([]);
    setCities([]);

    // reload for fresh code + countries
    loadInit();
  };

  const handleSave = async () => {
    if (!validate()) return;

    const payload = {
      workLocationId: isEdit ? workLocationId : 0,
      loginUserId: toInt(loginUserId, 0),
      branchId: toInt(branchId, 0),

      workLocName: toStr(formData.workLocName),
      code: toStr(formData.code),

      address1: toStr(formData.address1),
      address2: toStr(formData.address2),

      countryId: toInt(formData.countryId, 0),
      stateId: toInt(formData.stateId, 0),
      districtId: toInt(formData.districtId, 0),
      cityId: toInt(formData.cityId, 0),

      zipcode: toStr(formData.zipcode),
      contactPerson: toStr(formData.contactPerson),
      email: toStr(formData.email),
      mobile: toStr(formData.mobile),
      phone: toStr(formData.phone),
      remarks: toStr(formData.remarks),
      isClosed: !!formData.isClosed,

      branchType: branch,
    };

    try {
      setLoading(true);

      const { data } = await axios.post(`${API_BASE}/save`, payload, {
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Save failed");
      }

      toast.success(
        data?.message ||
          (isEdit ? "Updated Successfully" : "Inserted Successfully")
      );

      setTimeout(() => {
        navigate("/vehicle/WorkLocationListing");
      }, 700);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err?.message || "Save failed"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">WORK LOCATION MASTER</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black`
          }`}
        >
          {/* Header */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-8">
            <div className="flex items-center gap-4 flex-wrap">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                {isEdit ? "Edit Work Location" : "Add Work Location"}
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isEdit
                    ? isDark
                      ? "bg-yellow-900 text-yellow-100"
                      : "bg-yellow-100 text-yellow-800"
                    : isDark
                    ? "bg-blue-900 text-blue-100"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {isEdit ? `Edit Mode (#${workLocationId})` : "New Entry"}
              </span>
            </div>

            <button
              type="button"
              onClick={() => navigate("/vehicle/WorkLocationListing")}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-800"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <FaArrowLeft className="w-4 h-4" />
              Back to Listing
            </button>
          </div>

          {/* Form */}
          <div
            className={`rounded-xl p-6 border ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
            }`}
          >
            {pageLoading ? (
              <div className="text-center py-10">Loading...</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Left */}
                <div className="space-y-6">
                  <div>
                    <label className={labelClass}>
                      Work-Loc Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.workLocName}
                      onChange={(e) => setField("workLocName", e.target.value)}
                      className={inputClass}
                      placeholder="Enter work location name"
                      disabled={loading || pageLoading}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>
                      Address1 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={formData.address1}
                      onChange={(e) => setField("address1", e.target.value)}
                      className={inputClass}
                      placeholder="Enter primary address"
                      rows="3"
                      disabled={loading || pageLoading}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>
                      Country <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.countryId}
                      onChange={onCountryChange}
                      className={selectClass}
                      disabled={loading || pageLoading || loadingDDL}
                    >
                      <option value="">Select</option>
                      {countries.map((c) => (
                        <option key={c.id} value={String(c.id)}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>
                      District <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.districtId}
                      onChange={onDistrictChange}
                      className={selectClass}
                      disabled={
                        loading || pageLoading || loadingDDL || !formData.stateId
                      }
                    >
                      <option value="">Select</option>
                      {districts.map((d) => (
                        <option key={d.id} value={String(d.id)}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Zipcode</label>
                    <input
                      type="text"
                      value={formData.zipcode}
                      onChange={(e) => setField("zipcode", e.target.value)}
                      className={inputClass}
                      placeholder="Enter zipcode"
                      disabled={loading || pageLoading}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setField("email", e.target.value)}
                      className={inputClass}
                      placeholder="Enter email address"
                      disabled={loading || pageLoading}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Mobile</label>
                    <input
                      type="text"
                      value={formData.mobile}
                      onChange={(e) => setField("mobile", e.target.value)}
                      className={inputClass}
                      placeholder="Enter mobile number"
                      disabled={loading || pageLoading}
                    />
                  </div>
                </div>

                {/* Right */}
                <div className="space-y-6">
                  <div>
                    <label className={labelClass}>Code</label>
                    <input
                      type="text"
                      value={formData.code}
                      className={inputClass}
                      readOnly
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Address2</label>
                    <textarea
                      value={formData.address2}
                      onChange={(e) => setField("address2", e.target.value)}
                      className={inputClass}
                      placeholder="Enter secondary address"
                      rows="3"
                      disabled={loading || pageLoading}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>
                      State <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.stateId}
                      onChange={onStateChange}
                      className={selectClass}
                      disabled={
                        loading || pageLoading || loadingDDL || !formData.countryId
                      }
                    >
                      <option value="">Select</option>
                      {states.map((s) => (
                        <option key={s.id} value={String(s.id)}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>
                      City <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.cityId}
                      onChange={(e) => setField("cityId", e.target.value)}
                      className={selectClass}
                      disabled={
                        loading ||
                        pageLoading ||
                        loadingDDL ||
                        !formData.districtId
                      }
                    >
                      <option value="">Select</option>
                      {cities.map((c) => (
                        <option key={c.id} value={String(c.id)}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>
                      Contact Person <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.contactPerson}
                      onChange={(e) => setField("contactPerson", e.target.value)}
                      className={inputClass}
                      placeholder="Enter contact person name"
                      disabled={loading || pageLoading}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.phone}
                      onChange={(e) => setField("phone", e.target.value)}
                      className={inputClass}
                      placeholder="Enter phone number"
                      disabled={loading || pageLoading}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Remarks</label>
                    <textarea
                      value={formData.remarks}
                      onChange={(e) => setField("remarks", e.target.value)}
                      className={inputClass}
                      placeholder="Enter any remarks"
                      rows="3"
                      disabled={loading || pageLoading}
                    />
                  </div>

                  <div className="flex items-center gap-3 pt-2">
                    <input
                      id="isClosed"
                      type="checkbox"
                      checked={!!formData.isClosed}
                      onChange={(e) => setField("isClosed", e.target.checked)}
                      disabled={loading || pageLoading}
                    />
                    <label htmlFor="isClosed" className={labelClass + " mb-0"}>
                      Closed
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Info */}
            {!pageLoading ? (
              <div className="mt-8">
                <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Country → State → District → City selection is mandatory. City loads only from selected district.
                </div>
              </div>
            ) : null}

            {/* Buttons */}
            <div className="flex justify-end gap-4 pt-6 mt-8 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={handleReset}
                disabled={loading || pageLoading}
                className="px-6 py-3 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <FaUndo className="w-4 h-4" />
                {isEdit ? "Reload" : "Clear"}
              </button>

              <button
                type="button"
                onClick={handleSave}
                disabled={loading || pageLoading || loadingDDL || !canSave}
                className={`flex items-center gap-2 px-8 py-3 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  loading || pageLoading || loadingDDL || !canSave
                    ? "bg-gray-400 cursor-not-allowed"
                    : `${buttonClass} ${hoverClass}`
                }`}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <FaSave className="w-4 h-4" />
                    {isEdit ? "Update" : "Save"}
                  </>
                )}
              </button>
            </div>
          </div>

          <div className={`mt-8 pt-6 border-t ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <p className={`text-sm text-center ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              INFRA SCREEN : WORK LOCATION MASTER
            </p>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default WorkLocationMaster;